import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared primitives for the ruled spec-block idiom — the "official-record"
 * container used by the vehicle dossier and the service-entry detail.
 * See `.claude/DESIGN.md` (container-idiom doctrine, 2026-06-19).
 *
 * Pure extraction: these reproduce the previously-inlined chrome + rows
 * byte-for-byte. ModificationsSection borrows only `specLabelStyle` /
 * `specValueMono` — it is not a card-boxed spec-block.
 */

// Mono row label — mirrors the ServiceLog ledger colHeaderStyle vocabulary.
export const specLabelStyle: CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: T.textMuted,
};

// Mono value — machine identifiers / measured quantities (tabular figures).
export const specValueMono: CSSProperties = {
  fontFamily: F.mono,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13,
  color: T.text,
};

// Body value — prose facts (names, vendors).
export const specValueBody: CSSProperties = {
  fontFamily: F.body,
  fontSize: 14,
  color: T.text,
};

// The ruled spec-block chrome. Renders ONE <div>; inner zones supply their own
// padding. overflow:hidden by default so the rounded corners clip ruled rows;
// override (e.g. padded, overflow:visible) via `style`.
export function SpecBlock({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.md,
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface SpecRow {
  label: string;
  value: ReactNode;
  /** mono (default) for identifiers/quantities; false → body for prose. */
  mono?: boolean;
  /** 'baseline' (default); 'center' for pill-wrap values. */
  align?: 'baseline' | 'center';
  /** merged into the value cell (e.g. { wordBreak: 'break-all' } for VINs). */
  valueStyle?: CSSProperties;
}

// A ruled rows region: a borderTop opener + present-only rows; the last row
// carries no bottom rule. The CALLER pre-filters to present rows (so absent
// optional fields produce no row). Returns null when rows is empty.
export function SpecRows({ rows, labelCol = '88px' }: { rows: SpecRow[]; labelCol?: string }) {
  if (rows.length === 0) return null;
  return (
    <div style={{ borderTop: `1px solid ${T.border}` }}>
      {rows.map((row, idx) => (
        <div
          key={row.label}
          style={{
            display: 'grid',
            gridTemplateColumns: `${labelCol} 1fr`,
            gap: `0 ${SPACE.sm}px`,
            alignItems: row.align ?? 'baseline',
            padding: `10px ${SPACE.md}px`,
            borderBottom: idx < rows.length - 1 ? `1px solid ${T.border}` : 'none',
          }}
        >
          <span style={specLabelStyle}>{row.label}</span>
          <span
            style={{ ...(row.mono === false ? specValueBody : specValueMono), ...row.valueStyle }}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
